import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import apiClient from '../api/client';
import { getVendorLogo } from '../utils';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

export default function VendorSKUStatus() {
    const [summary, setSummary] = useState([]);
    const [loading, setLoading] = useState(true);

    const getSKUSummary = async () => {
        try {
            const res = await apiClient.get('/skurefreshsummary');
            setSummary(res.data || []);
        } catch (err) {
            console.error('Error fetching SKU summary:', err);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        getSKUSummary();
    }, []);

    return (
        <Paper sx={{ p: 2.5, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StorefrontOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Vendor Catalog Status
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {summary.length} Vendors Monitored
                </Typography>
            </Box>

            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Last SKU Created</TableCell>
                            <TableCell>Days Since Refresh</TableCell>
                            <TableCell align="right">Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            [1, 2, 3].map((row) => (
                                <TableRow key={row}>
                                    <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                                    <TableCell align="right"><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                                </TableRow>
                            ))
                        ) : summary.length > 0 ? (
                            summary.map((vendor) => {
                                const isDelayed = vendor.days_since_creation > 2;
                                return (
                                    <TableRow
                                        key={vendor.domain}
                                        sx={{
                                            '&:hover': { bgcolor: 'action.hover' },
                                            transition: 'background-color 0.15s ease',
                                        }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box
                                                    component="img"
                                                    src={getVendorLogo(vendor.domain)}
                                                    alt={vendor.domain}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '50%',
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        objectFit: 'contain',
                                                        bgcolor: 'common.white',
                                                        p: 0.25,
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {vendor.domain}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                            {vendor.latest_sku_created || '—'}
                                        </TableCell>

                                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                            {vendor.latest_sku_refresh ?? '—'}
                                        </TableCell>

                                        <TableCell align="right">
                                            {isDelayed ? (
                                                <Chip
                                                    icon={<WarningAmberOutlinedIcon style={{ fontSize: 14 }} />}
                                                    label="Delayed"
                                                    size="small"
                                                    color="error"
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
                                                    label="Healthy"
                                                    size="small"
                                                    color="success"
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    No vendor status data available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}
